import {
  EventType,
  LogLevel,
  PublicClientApplication,
  type AuthenticationResult,
  type Configuration
} from '@azure/msal-browser';

/**
 * Entra External ID MSAL configuration.
 *
 * NOTE:
 * - clientId / scope are not secrets, but they are environment-specific.
 * - keep this STRICT (no hidden defaults) so misconfigurations fail fast in CI.
 */

function requiredEnv(name: string): string {
  const v = process.env[name];
  // Jest/node: allow imports without requiring build-time env wiring.
  // Tests should not depend on real Entra config.
  if ((!v || !v.trim()) && process.env.NODE_ENV === 'test') return `__test__${name}`;
  if (!v || !v.trim()) throw new Error(`[msal] Missing required env var: ${name}`);
  return v;
}

const tenantId = requiredEnv('REACT_APP_ENTRA_TENANT_ID');
const tenantName = requiredEnv('REACT_APP_ENTRA_TENANT_NAME');

// Default to Entra External ID (ciamlogin) discovery:
// https://<tenantName>.ciamlogin.com/<tenantId>/v2.0/.well-known/openid-configuration
const defaultAuthority = `https://${tenantName}.ciamlogin.com/${tenantId}/v2.0`;

// CRITICAL: prefer build-time redirect URI (must match Entra config exactly).
// Falls back to origin-based callback for local dev convenience.
const redirectUri =
  process.env.REACT_APP_ENTRA_REDIRECT_URI ||
  (typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : 'http://localhost/auth/callback');

export const entraClientId =
  requiredEnv('REACT_APP_ENTRA_CLIENT_ID');

export const entraApiScope =
  requiredEnv('REACT_APP_ENTRA_API_SCOPE');

export const msalConfig: Configuration = {
  auth: {
    clientId: entraClientId,
    authority: process.env.REACT_APP_ENTRA_AUTHORITY || defaultAuthority,
    knownAuthorities: [process.env.REACT_APP_ENTRA_KNOWN_AUTHORITY || `${tenantName}.ciamlogin.com`],
    redirectUri,
    postLogoutRedirectUri: redirectUri,
    navigateToLoginRequestUrl: true
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
      loggerCallback: (level, message) => {
        if (level <= LogLevel.Warning) {
          // keep logs minimal in prod; increase when debugging auth issues
          // eslint-disable-next-line no-console
          console.warn(`[msal] ${message}`);
        }
      }
    }
  }
};

export const loginRequest = {
  scopes: ['openid', 'profile', 'email', entraApiScope]
};

function hasBrowserCrypto(): boolean {
  // MSAL requires WebCrypto; in Jest/node this is typically missing and would throw.
  const w = typeof window !== 'undefined' ? window : undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cryptoObj = (w as any)?.crypto;
  return Boolean(cryptoObj && cryptoObj.subtle);
}

export const msalInstance: PublicClientApplication | null = hasBrowserCrypto()
  ? new PublicClientApplication(msalConfig)
  : null;

// Keep an "active account" set so acquireTokenSilent works consistently.
msalInstance?.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload && 'account' in event.payload) {
    const account = (event.payload as AuthenticationResult).account;
    if (account) msalInstance.setActiveAccount(account);
  }
});

